import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  BookOpen,
  Clock,
  Users,
  Star,
  Search,
  Play,
  CheckCircle,
  MoreVertical,
  Award,
} from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { eCourseService } from "../../services/elearning/eCourseService";
import Pagination from "../../components/common/Pagination";

interface EnrolledCourse {
  _id: string;
  studentId: string;
  courseId: string;
  progress: number;
  status:
    | "pending"
    | "approved"
    | "active"
    | "completed"
    | "dropped"
    | "suspended";
  enrolledAt: string;
  lastAccessedAt?: string;
  completedAt?: string;
  // Populated course data
  course: {
    _id: string;
    title: string;
    description: string;
    shortDescription?: string;
    instructor: {
      _id: string;
      firstName: string;
      lastName: string;
      email?: string;
    };
    category: string;
    level: "Beginner" | "Intermediate" | "Advanced";
    duration: {
      estimatedHours: number;
      estimatedMinutes: number;
    };
    pricing: {
      type: "free" | "paid";
      amount: number;
      currency: string;
    };
    thumbnail?: string;
    language: string;
    rating?: number;
    reviewCount?: number;
  };
}

const MyEcourses: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 9,
    total: 0,
    pages: 0,
    hasNext: false,
    hasPrev: false,
  });

  // Fetch enrolled courses
  const { data: enrolledCoursesData, isLoading } = useQuery({
    queryKey: ["student-enrolled-courses"],
    queryFn: () => eCourseService.getMyEnrolledCourses(),
  });

  const enrolledCourses: EnrolledCourse[] = enrolledCoursesData?.data || [];

  // Filter courses based on search and status
  const filteredCourses = enrolledCourses.filter((enrollment) => {
    const matchesSearch = enrollment.course.title
      .toLowerCase()
      .includes(searchTerm.toLowerCase());

    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "in-progress" &&
        enrollment.progress > 0 &&
        enrollment.progress < 100) ||
      (filterStatus === "completed" && enrollment.progress === 100) ||
      (filterStatus === "not-started" && enrollment.progress === 0);

    return matchesSearch && matchesStatus;
  });

  // Update pagination when filteredCourses changes
  React.useEffect(() => {
    const total = filteredCourses.length;
    const pages = Math.ceil(total / pagination.limit);
    setPagination((prev) => ({
      ...prev,
      total,
      pages,
      hasNext: prev.page < pages,
      hasPrev: prev.page > 1,
    }));
  }, [filteredCourses.length, pagination.limit, pagination.page]);

  // Calculate paginated courses
  const paginatedCourses = filteredCourses.slice(
    (pagination.page - 1) * pagination.limit,
    pagination.page * pagination.limit
  );

  const handleCourseClick = (enrollment: EnrolledCourse) => {
    // Continue learning from where student left off
    if (enrollment.progress > 0) {
      navigate(`/student/elearning/course/${enrollment.course._id}/continue`);
    } else {
      navigate(`/student/elearning/course/${enrollment.course._id}/start`);
    }
  };

  const getStatusBadge = (enrollment: EnrolledCourse) => {
    if (enrollment.progress === 100) {
      return (
        <span className="inline-flex items-center px-3 py-1.5 text-xs font-bold bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full shadow-lg backdrop-blur-sm">
          <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
          Completed
        </span>
      );
    } else if (enrollment.progress > 0) {
      return (
        <span className="inline-flex items-center px-3 py-1.5 text-xs font-bold bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-full shadow-lg backdrop-blur-sm">
          <Play className="w-3.5 h-3.5 mr-1.5" />
          In Progress
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center px-3 py-1.5 text-xs font-bold bg-white/95 backdrop-blur-sm text-slate-700 rounded-full shadow-lg border border-slate-200">
          <BookOpen className="w-3.5 h-3.5 mr-1.5" />
          Not Started
        </span>
      );
    }
  };

  const renderStars = (rating: number = 0) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating ? "text-yellow-400 fill-current" : "text-gray-300"
            }`}
          />
        ))}
      </div>
    );
  };

  const formatLastAccessed = (date?: string) => {
    if (!date) return "Never accessed";
    const accessDate = new Date(date);
    const now = new Date();
    const diffInDays = Math.floor(
      (now.getTime() - accessDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffInDays === 0) return "Today";
    if (diffInDays === 1) return "Yesterday";
    if (diffInDays < 7) return `${diffInDays} days ago`;
    return accessDate.toLocaleDateString();
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header - Modern & Clean */}
        <div className="mb-10">
          <h1 className="text-2xl md:text-4xl font-bold text-slate-900 mb-3 leading-tight">
            My E-Courses
          </h1>
          <p className="text-slate-600 text-lg">
            Continue your learning journey. You have {enrolledCourses.length}{" "}
            enrolled course{enrolledCourses.length !== 1 ? "s" : ""}
          </p>
        </div>

        {/* Stats Cards - Modern Design */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-6 hover:shadow-lg transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600 mb-1">
                  Total Courses
                </p>
                <p className="text-3xl font-bold text-slate-900">
                  {enrolledCourses.length}
                </p>
              </div>
              <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-500 text-white shadow-lg">
                <BookOpen className="w-7 h-7" />
              </div>
            </div>
          </div>

          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-6 hover:shadow-lg transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600 mb-1">
                  In Progress
                </p>
                <p className="text-3xl font-bold text-slate-900">
                  {
                    enrolledCourses.filter(
                      (e) => e.progress > 0 && e.progress < 100
                    ).length
                  }
                </p>
              </div>
              <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-lg">
                <Play className="w-7 h-7" />
              </div>
            </div>
          </div>

          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-6 hover:shadow-lg transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600 mb-1">
                  Completed
                </p>
                <p className="text-3xl font-bold text-slate-900">
                  {enrolledCourses.filter((e) => e.progress === 100).length}
                </p>
              </div>
              <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 text-white shadow-lg">
                <CheckCircle className="w-7 h-7" />
              </div>
            </div>
          </div>

          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-6 hover:shadow-lg transition-all duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600 mb-1">
                  Avg Progress
                </p>
                <p className="text-3xl font-bold text-slate-900">
                  {enrolledCourses.length > 0
                    ? Math.round(
                        enrolledCourses.reduce(
                          (sum, e) => sum + e.progress,
                          0
                        ) / enrolledCourses.length
                      )
                    : 0}
                  %
                </p>
              </div>
              <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-lg">
                <Award className="w-7 h-7" />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter - Modern Pills */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-6 mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search your courses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-slate-50/50 hover:bg-white"
                />
              </div>
            </div>

            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-5 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-slate-50/50 hover:bg-white font-medium text-slate-700"
            >
              <option value="all">All Courses</option>
              <option value="not-started">Not Started</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        {/* Courses Grid - Modern Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedCourses.map((enrollment) => (
            <div
              key={enrollment._id}
              className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 hover:shadow-xl transition-all duration-300 cursor-pointer group overflow-hidden"
              onClick={() => handleCourseClick(enrollment)}
            >
              {/* Course Thumbnail */}
              <div className="relative overflow-hidden">
                <img
                  src={
                    enrollment.course.thumbnail || "/api/placeholder/400/200"
                  }
                  alt={enrollment.course.title}
                  className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4">
                  {getStatusBadge(enrollment)}
                </div>
                {enrollment.progress > 0 && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent text-white p-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="font-semibold">
                        {enrollment.progress}% Complete
                      </span>
                      <span className="text-slate-200">
                        {formatLastAccessed(enrollment.lastAccessedAt)}
                      </span>
                    </div>
                    <div className="w-full bg-slate-700/50 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-indigo-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${enrollment.progress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Course Content */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-bold text-slate-900 line-clamp-2 leading-tight group-hover:text-blue-600 transition-colors">
                    {enrollment.course.title}
                  </h3>
                  <button className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all">
                    <MoreVertical className="w-4 h-4" />
                  </button>
                </div>

                <p className="text-sm text-slate-600 mb-5 line-clamp-2 leading-relaxed">
                  {enrollment.course.shortDescription ||
                    enrollment.course.description}
                </p>

                {/* Course Meta */}
                <div className="space-y-3 mb-5">
                  <div className="flex items-center text-sm text-slate-600">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 mr-2">
                      <Users className="w-4 h-4 text-slate-600" />
                    </div>
                    <span className="font-medium">
                      {enrollment.course.instructor.firstName}{" "}
                      {enrollment.course.instructor.lastName}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-sm text-slate-600">
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 mr-2">
                        <Clock className="w-4 h-4 text-slate-600" />
                      </div>
                      <span className="font-medium">
                        {enrollment.course.duration.estimatedHours}h
                      </span>
                    </div>

                    <div className="flex items-center">
                      {renderStars(enrollment.course.rating)}
                      <span className="text-sm text-slate-600 ml-2 font-medium">
                        ({enrollment.course.reviewCount || 0})
                      </span>
                    </div>
                  </div>
                </div>

                {/* Action Button */}
                <button className="w-full py-3 px-4 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 flex items-center justify-center gap-2 font-semibold shadow-sm hover:shadow-md">
                  {enrollment.progress > 0 ? (
                    <>
                      <Play className="w-5 h-5" />
                      Continue Learning
                    </>
                  ) : (
                    <>
                      <BookOpen className="w-5 h-5" />
                      Start Course
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {filteredCourses.length > 0 && (
          <div className="mt-8 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-6">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.pages}
              onPageChange={(page) => setPagination({ ...pagination, page })}
              pageSize={pagination.limit}
              onPageSizeChange={(size) => {
                setPagination({ ...pagination, limit: size, page: 1 });
              }}
              showPageSizeSelector={true}
              className="w-full"
            />
          </div>
        )}

        {/* Summary */}
        {filteredCourses.length > 0 && (
          <div className="mt-4 bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-lg p-4">
            <p className="text-sm text-slate-700 font-semibold">
              Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
              {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
              of {pagination.total} courses
            </p>
          </div>
        )}

        {/* Empty State - Modern Design */}
        {filteredCourses.length === 0 && !isLoading && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="flex items-center justify-center w-24 h-24 rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 mb-6 shadow-lg">
              <BookOpen className="w-12 h-12 text-slate-600" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-3">
              {searchTerm || filterStatus !== "all"
                ? "No courses match your filters"
                : "No enrolled courses yet"}
            </h3>
            <p className="text-slate-600 mb-8 max-w-md text-lg">
              {searchTerm || filterStatus !== "all"
                ? "Try adjusting your search or filter criteria"
                : "Browse the course catalog to find courses that interest you"}
            </p>
            {!searchTerm && filterStatus === "all" && (
              <button
                onClick={() => navigate("/student/elearning/catalog")}
                className="px-8 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl"
              >
                Browse Courses
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyEcourses;
